import axios from '@shared/axios-shared';
import { unwrap } from './errors';

// Pre-auth (email-link) endpoints — throttled server-side.
export const getLinkContext = (t) => axios.get('/api/v2/hosted/billing/context', { params: { t } }).then(unwrap);

export const verifyLink = (t, studentLastName) =>
	axios.post('/api/v2/hosted/billing/verify', { t, student_last_name: studentLastName }).then(unwrap);

// Self-serve fresh link for tokenless visits or expired emailed links.
// Constant 202 response — never reveals whether the email matched.
export const requestManageLink = (email) => axios.post('/api/v2/hosted/billing/request-link', { email }).then(unwrap);

// Post-Checkout result. Stripe's redirect carries no session token, so the
// page reads the outcome back with the session id (the secret) and the
// invoice id (routing) it was sent. Read-only; safe to repeat on reload.
export const getCheckoutResult = (sessionId, invoiceId) =>
	axios
		.get('/api/v2/hosted/billing/checkout-session', { params: { session_id: sessionId, invoice: invoiceId } })
		.then(unwrap);

// Session-scoped parent billing endpoints (Bearer header set by session.js).
export const getAutoPayStatus = (studentId) =>
	axios.get('/api/v2/parent/billing/auto-pay', { params: { student_id: studentId } }).then(unwrap);

// GLOBAL (payer+student) toggle — one switch covers every plan, incl.
// future ones. Payload: { student_id, enabled, mandate_accepted?,
// payment_method_id? } — when enabling, the server promotes
// payment_method_id to the payer's default before any plan flips.
export const toggleAutoPay = (payload) => axios.post('/api/v2/parent/billing/auto-pay', payload).then(unwrap);

export const listPaymentMethods = (studentId) =>
	axios.get('/api/v2/parent/billing/payment-methods', { params: { student_id: studentId } }).then(unwrap);

export const createSetupIntent = (studentId) =>
	axios.post('/api/v2/parent/billing/payment-methods/setup-intent', { student_id: studentId }).then(unwrap);

export const setDefaultPaymentMethod = (paymentMethodId, studentId) =>
	axios
		.patch(`/api/v2/parent/billing/payment-methods/${paymentMethodId}/default`, { student_id: studentId })
		.then(unwrap);

const PAYABLE_STATUSES = ['due', 'past_due'];

const listInvoicesWithStatuses = (studentId, statuses) =>
	axios.get('/api/v2/parent/billing/invoices', { params: { student_id: studentId, status: statuses } }).then(unwrap);

// `processing` is a bank debit still settling; the screen shows it instead of
// offering to pay the same balance again. An API that does not know the
// status yet answers 422, so the request falls back to the payable statuses
// alone and the page behaves as it did before, whichever side deploys first.
export const listOpenInvoices = async (studentId) => {
	try {
		return await listInvoicesWithStatuses(studentId, [...PAYABLE_STATUSES, 'processing']);
	} catch (err) {
		if (err?.response?.status !== 422) throw err;
		return listInvoicesWithStatuses(studentId, PAYABLE_STATUSES);
	}
};

export const getInvoiceStripeUrl = (invoiceId) =>
	axios.get(`/api/v2/parent/billing/invoices/${invoiceId}/stripe-url`).then(unwrap);
